import { startPrescriptionCreatorOver } from '@features/fields/components/PrescriptionCreatorPane'
import {
  reportLink,
  resolveRequestedSeason,
  sectionsForReport
} from '@features/fields/components/Reporting/ReportsSearchParams'
import { useReportingSeasons } from '@features/fields/components/Reporting/useReportingSeasons'
import { requestBoundaryAutogen } from '@features/fields/lib/boundaryAutogenRequest'
import {
  DealerRestrictedDenial,
  dealerRestrictedDenial
} from '@features/operations/components/DealerRestrictedGuard'
import { isDealerRestrictedPath } from '@features/operations/routes'
import { BoundaryCreatorOutcome, PrescriptionZoningOutcome } from 'api-validations/aurora'
import { ExportSurface } from 'api-validations/data-export'
import { ImportSurface } from 'api-validations/data-import'
import {
  createPlanningCffsApi,
  getAllBoundariesApi,
  getPlanningCffsApi
} from 'api-validations/locutus'
import { fieldsApi, flattenStreams } from 'api-validations/pangea'
import { useCallback, useMemo } from 'react'
import { generatePath, useNavigate } from 'react-router'
import { OrgLevelPermissions, useOrgCodeOrThrow, useOrgInfo } from 'shared-ui/active-operation'
import { invalidationKeysFor, useApiClient, useQueryClient } from 'shared-ui/api-provider'
import {
  type AuroraDispatchHandlerMap,
  describeCurrentView,
  toAnalysisFieldAddress
} from 'shared-ui/aurora'
import { type MapAddress, mapLink, type RequestedMapAction, useMapAction } from 'shared-ui/map'
import { isNil, isNonEmpty, isNonNil, type NonEmptyArray } from 'utils'
import { cffKey, sameCff, unregisteredCffs } from 'utils/cff'

import { path, SELECT_UNREGISTERED_FIELDS_PARAM } from '../../../routes/paths'
import { auroraControls } from '../auroraControls/auroraControlsStore'

const sameName = (a: string, b: string) =>
  a.trim().localeCompare(b.trim(), undefined, { sensitivity: 'base' }) === 0

const IMPORT_SCREEN_PATHS: Record<ImportSurface, string> = {
  [ImportSurface.Prescriptions]: path.prescriptions,
  [ImportSurface.Boundaries]: path.boundaries,
  [ImportSurface.FieldList]: path.fields,
  [ImportSurface.AsAppliedData]: path.applicationDataUpload
}

const IMPORT_DENIAL_REASONS: Record<DealerRestrictedDenial, string> = {
  [DealerRestrictedDenial.DealerOrg]:
    'Panorama does not give dealer organizations this import screen.',
  [DealerRestrictedDenial.NotAManager]:
    'Only members who can manage the operation, and supporters, can open this import screen.'
}

const EXPORT_SCREEN_PATHS: Record<ExportSurface, string> = {
  [ExportSurface.Prescriptions]: path.prescriptions,
  [ExportSurface.Boundaries]: path.boundaries,
  [ExportSurface.FieldList]: path.fields,
  [ExportSurface.AsAppliedData]: path.fieldUpdate
}

const EXPORT_DENIAL_REASONS: Record<DealerRestrictedDenial, string> = {
  [DealerRestrictedDenial.DealerOrg]:
    'Dealerships do not have this export screen; only farming operations do.',
  [DealerRestrictedDenial.NotAManager]:
    'Only members who can manage the operation, and supporters, can open this export screen.'
}

const REGISTRATION_DENIAL_REASONS: Record<DealerRestrictedDenial, string> = {
  [DealerRestrictedDenial.DealerOrg]:
    'Panorama does not let dealer organizations add fields to planning.',
  [DealerRestrictedDenial.NotAManager]:
    'Only members who can manage the operation can add fields to planning.'
}

const isOnMap = (orgCode: string) =>
  window.location.pathname === generatePath(path.map, { orgCode })

const nonEmptyOrNull = <T>(values: T[] | null): NonEmptyArray<T> | null => {
  const list = values ?? undefined
  return isNonEmpty(list) ? list : null
}

/**
 * The web client's handlers for the client-dispatched tools Aurora can call
 * (Alert, SetAnalysisField, the report/import/export deep links, etc.).
 * Shared between the WebSocket path — dispatched over the wire and consumed
 * by AuroraClientHandlers — and the WebRTC path, which calls these directly
 * as ordinary browser tools since its RealtimeSession already runs here.
 *
 * PoC scope: Alert renders via `window.alert`. Future iterations will swap
 * in a toast / notification panel rather than a blocking modal.
 */
export const useAuroraClientDispatchHandlers = (): AuroraDispatchHandlerMap => {
  const navigate = useNavigate()
  const orgCode = useOrgCodeOrThrow()
  const orgInfo = useOrgInfo()
  const seasons = useReportingSeasons()
  const apiClient = useApiClient()
  const queryClient = useQueryClient()
  const { mutateAsync: runMapAction } = useMapAction()

  const openOnMap = useCallback(
    async ({ action, address }: { action: RequestedMapAction; address: MapAddress }) => {
      if (!isOnMap(orgCode)) {
        navigate(mapLink(generatePath(path.map, { orgCode }), address))
        return true
      }
      return (await runMapAction(action)).kind === 'Landed'
    },
    [navigate, orgCode, runMapAction]
  )

  return useMemo(
    () => ({
      Alert: ({ message }) => {
        window.alert(message)
        return { shown: true }
      },
      EchoBack: ({ text }) => ({ echo: text, receivedAt: new Date().toISOString() }),
      DescribeCurrentView: () => describeCurrentView({ isOnMap: isOnMap(orgCode) }),
      // Deep-link into the map page rather than mutate the map store directly,
      // so the agent can open a field from anywhere in the app.
      SetAnalysisField: (args) => {
        navigate(mapLink(generatePath(path.map, { orgCode }), toAnalysisFieldAddress(args)))
        return { switched: true }
      },
      // Deep-link into the reporting page rather than drive its controls: the page builds itself
      // from search params.
      OpenReport: ({
        reportType,
        season,
        crop,
        client,
        farm,
        field,
        dateRange,
        tasks,
        deviceSerialNumbers,
        includedData
      }) => {
        const requestedSeason = resolveRequestedSeason({ year: season, crop, seasons })

        if (requestedSeason === undefined) {
          return {
            opened: false,
            reason: isNil(crop)
              ? `This operation has no ${season} season to report on.`
              : `This operation has no ${crop} data for ${season}.`
          }
        }

        const { sections, unsupported } = sectionsForReport({
          reportType,
          requested: includedData ?? []
        })

        const url = reportLink(generatePath(path.reporting, { orgCode }), {
          reportType,
          season,
          crop: requestedSeason.crop,
          client,
          farm,
          field,
          dateRange,
          tasks: nonEmptyOrNull(tasks),
          deviceSerialNumbers: nonEmptyOrNull(deviceSerialNumbers),
          includedData: nonEmptyOrNull(sections)
        })

        navigate(url)
        return { opened: true, url, unsupportedIncludedData: unsupported }
      },
      // The planning screens sit behind a route guard, so a user the guard would
      // refuse gets the refusal here instead of a 403 page the agent describes as
      // an open import screen.
      OpenImportScreen: ({ surface }) => {
        const routePath = IMPORT_SCREEN_PATHS[surface]

        if (isDealerRestrictedPath(routePath)) {
          if (isNil(orgInfo)) {
            return {
              opened: false,
              reason: 'This operation is still loading; ask the user to try again in a moment.'
            }
          }

          const denial = dealerRestrictedDenial(orgInfo)
          if (isNonNil(denial)) {
            return { opened: false, reason: IMPORT_DENIAL_REASONS[denial] }
          }
        }

        const url = generatePath(routePath, { orgCode })
        navigate(url)
        return { opened: true, url }
      },
      OpenExportScreen: ({ surface }) => {
        const routePath = EXPORT_SCREEN_PATHS[surface]

        if (isDealerRestrictedPath(routePath)) {
          if (isNil(orgInfo)) {
            return {
              opened: false,
              reason: 'This operation is still loading; ask the user to try again in a moment.'
            }
          }

          const denial = dealerRestrictedDenial(orgInfo)
          if (isNonNil(denial)) {
            return { opened: false, reason: EXPORT_DENIAL_REASONS[denial] }
          }
        }

        const url = generatePath(routePath, { orgCode })
        navigate(url)
        return { opened: true, url }
      },
      OpenFieldRegistration: () => {
        if (isNil(orgInfo)) {
          return {
            opened: false,
            reason: 'This operation is still loading; ask the user to try again in a moment.'
          }
        }

        const denial = dealerRestrictedDenial(orgInfo)
        if (isNonNil(denial)) return { opened: false, reason: REGISTRATION_DENIAL_REASONS[denial] }

        const url = `${generatePath(path.fields, { orgCode })}?${SELECT_UNREGISTERED_FIELDS_PARAM}=all`
        navigate(url)
        return { opened: true, url }
      },
      RegisterFields: async ({ fields: requested }) => {
        if (isNil(orgInfo)) {
          return {
            registered: false,
            reason: 'This operation is still loading; ask the user to try again in a moment.'
          }
        }

        const denial = dealerRestrictedDenial(orgInfo)
        if (isNonNil(denial))
          return { registered: false, reason: REGISTRATION_DENIAL_REASONS[denial] }
        if (OrgLevelPermissions.isSupporter(orgInfo)) {
          return {
            registered: false,
            reason: "Supporters can view this operation's planning fields but cannot add to them."
          }
        }

        const [catalog, planning] = await Promise.all([
          apiClient.callApi(fieldsApi, { orgCode }),
          apiClient.callApi(getPlanningCffsApi, { orgCode })
        ])
        const waiting = new Map(
          unregisteredCffs({ fields: flattenStreams(catalog), planningFields: planning.items }).map(
            (cff) => [cffKey(cff), cff]
          )
        )
        const registered = new Set(planning.items.map(cffKey))
        const unknown = requested.filter(
          (cff) => !waiting.has(cffKey(cff)) && !registered.has(cffKey(cff))
        )
        if (isNonEmpty(unknown)) {
          return {
            registered: false,
            reason: `No field with passes is named ${unknown.map(({ field }) => field).join(', ')}; check the names against the field catalog.`
          }
        }

        const requestedKeys = new Set(requested.map(cffKey))
        const fields = [...waiting.values()].filter((cff) => requestedKeys.has(cffKey(cff)))
        if (fields.length > 0) {
          await apiClient.callApi(createPlanningCffsApi, { orgCode }, { fields })
          for (const queryKey of invalidationKeysFor(createPlanningCffsApi, { orgCode })) {
            queryClient.invalidateQueries({ queryKey })
          }
        }
        return { registered: true, count: fields.length }
      },
      OpenBoundaryCreator: async ({ client, farm, field, source }) => {
        if (isNil(orgInfo) || !OrgLevelPermissions.canManage(orgInfo)) {
          return {
            outcome: BoundaryCreatorOutcome.Failed,
            reason: 'Only members who can manage the operation can create boundaries.'
          }
        }

        const { items } = await apiClient.callApi(getPlanningCffsApi, { orgCode })
        const planningField = items.find((cff) => sameCff(cff, { client, farm, field }))
        if (planningField === undefined) return { outcome: BoundaryCreatorOutcome.NotRegistered }

        const generated = requestBoundaryAutogen({ planningFieldId: planningField.id, source })
        const opened = await openOnMap({
          action: { kind: 'EnterBoundaryCreator', planningFieldId: planningField.id },
          address: {
            kind: 'InField',
            main: { kind: 'BoundaryCreator', planningFieldId: planningField.id }
          }
        })
        if (!opened) {
          return {
            outcome: BoundaryCreatorOutcome.Failed,
            reason: 'The map could not open the boundary creator.'
          }
        }
        return generated
      },
      ShowControls: async ({ controls }) => auroraControls.show(controls),
      OpenPrescriptionZoning: async ({ client, farm, field }) => {
        if (isNil(orgInfo) || !OrgLevelPermissions.canManage(orgInfo)) {
          return {
            outcome: PrescriptionZoningOutcome.Failed,
            reason: 'Only members who can manage the operation can create prescriptions.'
          }
        }

        const { items } = await apiClient.callApi(getPlanningCffsApi, { orgCode })
        const matches = items.filter(
          (cff) =>
            sameName(cff.field, field) &&
            (client === null || sameName(cff.client, client)) &&
            (farm === null || sameName(cff.farm, farm))
        )
        const [planningField] = matches
        if (planningField === undefined) return { outcome: PrescriptionZoningOutcome.NotFound }
        if (matches.length > 1) {
          return {
            outcome: PrescriptionZoningOutcome.Ambiguous,
            fields: matches.map((cff) => ({ client: cff.client, farm: cff.farm, field: cff.field }))
          }
        }

        const boundaries = await apiClient.callApi(getAllBoundariesApi, { orgCode })
        const boundary = boundaries.items.find(
          ({ fieldId, isDefault }) => fieldId === planningField.id && isDefault
        )
        if (boundary === undefined) return { outcome: PrescriptionZoningOutcome.NoBoundary }

        startPrescriptionCreatorOver()
        const opened = await openOnMap({
          action: { kind: 'EnterPrescriptionCreator', planningFieldId: planningField.id },
          address: {
            kind: 'InField',
            main: { kind: 'PrescriptionCreator', planningFieldId: planningField.id }
          }
        })
        if (!opened) {
          return {
            outcome: PrescriptionZoningOutcome.Failed,
            reason: 'The map could not open the prescription screen.'
          }
        }
        const { client: fieldClient, farm: fieldFarm, field: fieldName } = planningField
        return {
          outcome: PrescriptionZoningOutcome.Opened,
          field: { client: fieldClient, farm: fieldFarm, field: fieldName },
          boundary: boundary.name
        }
      }
    }),
    [navigate, orgCode, orgInfo, seasons, apiClient, queryClient, openOnMap]
  )
}
